// M129 Grote Lijnen — Social Suite. TikTok + Facebook tabs.
// Genereren via /api/social/generate → toevoegen aan Publish Queue via
// /api/social/queue. Geen OAuth, geen publicatie.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { Sparkles, Send, Loader2 } from "lucide-react";

const PLATFORMS = [
  { key: "tiktok", label: "TikTok", testid: "tab-tiktok" },
  { key: "facebook", label: "Facebook", testid: "tab-facebook" },
];
const TONES = ["professioneel", "vriendelijk", "energiek", "direct", "informatief"];

export default function SocialSuitePage() {
  const [platform, setPlatform] = useState("tiktok");
  const [topic, setTopic] = useState("");
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState("professioneel");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [queueOk, setQueueOk] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { setResult(null); setErr(null); setQueueOk(false); }, [platform]);

  const generate = async () => {
    if (!topic.trim() || !goal.trim()) {
      setErr("Vul onderwerp en doel in.");
      return;
    }
    setGenerating(true); setErr(null); setResult(null); setQueueOk(false);
    try {
      const { data } = await api.post("/social/generate", { platform, topic, goal, tone });
      setResult(data);
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setErr(d?.reason_nl || "Content-generatie is nu niet mogelijk. Probeer het later opnieuw.");
    } finally {
      setGenerating(false);
    }
  };

  const addToQueue = async () => {
    if (!result) return;
    try {
      await api.post("/social/queue", { platform, topic, generated: result.generated });
      setQueueOk(true);
    } catch (e) {
      setErr("Toevoegen aan Publish Queue is niet gelukt.");
    }
  };

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-display text-text-primary tracking-tight" data-testid="social-page-title">Social Suite</h1>
        <p className="text-xs text-text-muted mt-1">CONCEPT-GENERATIE · ADVIESMODUS · GEEN PUBLICATIE</p>
      </div>

      <div className="flex gap-2 mb-4 border-b border-border-hairline">
        {PLATFORMS.map((p) => (
          <button key={p.key} onClick={() => setPlatform(p.key)} data-testid={p.testid}
                   className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${platform === p.key ? "border-accent-teal text-accent-teal" : "border-transparent text-text-muted hover:text-text-primary"}`}>
            {p.label}
          </button>
        ))}
      </div>

      <section className="rounded-card border border-border-hairline bg-bg-panel-raised p-5 mb-6" data-testid="social-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-text-muted block mb-1">Onderwerp</label>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="social-topic-input"
                    placeholder="Bijv. Nieuwe gietvloer-collectie voor woningen"
                    className="w-full text-sm bg-bg-panel border border-border-hairline rounded px-2 py-1.5 text-text-primary" />
          </div>
          <div>
            <label className="text-[10px] text-text-muted block mb-1">Doel</label>
            <input value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="social-goal-input"
                    placeholder="Bijv. Meer aanvragen voor showroom"
                    className="w-full text-sm bg-bg-panel border border-border-hairline rounded px-2 py-1.5 text-text-primary" />
          </div>
          <div>
            <label className="text-[10px] text-text-muted block mb-1">Toon</label>
            <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="social-tone-select"
                     className="w-full text-sm bg-bg-panel border border-border-hairline rounded px-2 py-1.5 text-text-primary">
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <button onClick={generate} disabled={generating} data-testid="social-generate-btn"
                 className="mt-4 flex items-center gap-2 px-4 py-2 rounded border border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal text-sm disabled:opacity-50">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Genereer content
        </button>
        {err && <div className="mt-3 text-xs text-warn-amber italic" data-testid="social-error">{err}</div>}
      </section>

      {result && (
        <section className="rounded-card border border-border-hairline bg-bg-panel p-5" data-testid="social-result">
          <div className="text-[10px] text-text-muted font-semibold mb-3">Gegenereerde content — {platform}</div>
          {Object.entries(result.generated || {}).map(([k, v]) => (
            <div key={k} className="mb-3">
              <div className="text-[10px] text-text-muted">{k}</div>
              <div className="text-sm text-text-primary whitespace-pre-wrap">{Array.isArray(v) ? v.join(" ") : String(v)}</div>
            </div>
          ))}
          <button onClick={addToQueue} disabled={queueOk} data-testid="social-queue-btn"
                   className="mt-2 flex items-center gap-2 px-4 py-2 rounded border border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal text-sm disabled:opacity-50">
            <Send className="w-4 h-4" /> {queueOk ? "Toegevoegd aan Publish Queue" : "Voeg toe aan Publish Queue"}
          </button>
        </section>
      )}
    </AppLayout>
  );
}
